﻿import {DateTime} from "@2d-front-monorepo/core";
import {createFakeLazy} from "@2d-front-monorepo/design-library";
import {
	AllPermitsResult,
	DriverId,
	DriverResult,
	FullEntityRelation,
	GroupedPermit,
	IntegraHolderData,
	IntegraZone,
	ParkingTariff,
	PermitResult, ResidencyId,
	ResidencyResult,
	VehicleId,
	VehicleResult
} from "@2d-front-monorepo/permits-business";
import {JSX, lazy, LazyExoticComponent} from "react";
import {useGlobalContext} from "../hooks/useGlobalContext";
import {EmptyComponent} from "./EmptyComponent";

const emptyComponent = createFakeLazy(EmptyComponent);

const permitHomes: Record<string, LazyExoticComponent<() => JSX.Element>> = {
	"pittsburgh": lazy(() => import("./pittsburgh/permits/Home")),
	"coral-gables": lazy(() => import("./coral-gables/permits/Home")),
	"gloucester": lazy(() => import("./gloucester/permits/Home")),
	"gloucester-longbeach": lazy(() => import("./gloucester/permits/Home")),
	"melbourne": lazy(() => import("./melbourne/permits/Home")),
	"canmore": lazy(() => import("./canmore/permits/Home")),
}

const residencies: Record<string, LazyExoticComponent<(props: CreateResidencyProps) => JSX.Element>> = {
	"_default_": lazy(() => import("./commons/residencies/Create"))
}

const vehicles: Record<string, LazyExoticComponent<(props: CreateVehicleProps) => JSX.Element>> = {
	"_default_": lazy(() => import("./commons/vehicles/Create")),
	"pittsburgh": lazy(() => import("./pittsburgh/vehicles/Create")),
}

const permitTopMenus: Record<string, LazyExoticComponent<(props: PermitsTopMenuProps) => JSX.Element>> = {
	"pittsburgh": lazy(() => import("./pittsburgh/permits/PermitsTopMenu"))
}

const permitMenus: Record<string, LazyExoticComponent<(props: PermitMenuProps) => JSX.Element>> = {
	"_default_": lazy(() => import("./commons/permits/./DefaultPermitMenu")),
	"pittsburgh": lazy(() => import("./pittsburgh/permits/PermitMenu")),
	"coral-gables": lazy(() => import("./coral-gables/permits/PermitMenu")),
	"gloucester": lazy(() => import("./gloucester/permits/PermitMenu")),
	"gloucester-longbeach": lazy(() => import("./gloucester/permits/PermitMenu")),
}

const permitFooters: Record<string, LazyExoticComponent<(props: PermitFooterProps) => JSX.Element>> = {
	"pittsburgh": lazy(() => import("./pittsburgh/permits/PermitFooter")),
	"coral-gables": lazy(() => import("./coral-gables/permits/PermitFooter")),
}

const permitPages: Record<string, LazyExoticComponent<(props: PermitPageProps) => JSX.Element>> = {
	"gloucester": lazy(() => import("./gloucester/permits/PermitPage")),
	"gloucester-longbeach": lazy(() => import("./gloucester/permits/PermitPage")),
}

export type CreateResidencyProps = {
	holderData: IntegraHolderData;
	permits: AllPermitsResult;
	residency?: ResidencyResult;
	permit?: PermitResult;
}

export type CreateVehicleProps = {
	holderData: IntegraHolderData;
	permits: AllPermitsResult;
	vehicle?: VehicleResult;
	permit?: PermitResult;
}

export type PermitsTopMenuProps = {
	permits: AllPermitsResult;
	holderData: IntegraHolderData;
};

export type DefaultPermitProps = {
	now: DateTime;
	tariff: ParkingTariff;
	zone: IntegraZone;
	group: GroupedPermit;
	permits: AllPermitsResult;
	holderData: IntegraHolderData;
	driver: FullEntityRelation<DriverId, DriverResult> | undefined;
	residency: FullEntityRelation<ResidencyId, ResidencyResult> | undefined;
	vehicles: FullEntityRelation<VehicleId, VehicleResult>[];
}

export type PermitMenuProps = DefaultPermitProps & {};
export type PermitFooterProps = DefaultPermitProps & {}
export type PermitPageProps = DefaultPermitProps & {}

export default class Registrations {
	private static _slug?: string;
	private static get slug(): string {
		return this._slug ??= useGlobalContext().installation.slug;
	}

	public static getCreateResidency(): LazyExoticComponent<(props: CreateResidencyProps) => JSX.Element> {
		return residencies[this.slug] ?? residencies["_default_"] ?? emptyComponent;
	}

	public static getCreateVehicle(): LazyExoticComponent<(props: CreateVehicleProps) => JSX.Element> {
		return vehicles[this.slug] ?? vehicles["_default_"] ?? emptyComponent;
	}

	public static getPermitFooter(): LazyExoticComponent<(props: PermitFooterProps) => JSX.Element> {
		return permitFooters[this.slug] ?? permitFooters["_default_"] ?? emptyComponent;
	}

	public static getPermitPage(): LazyExoticComponent<(props: PermitPageProps) => JSX.Element> {
		return permitPages[this.slug] ?? permitPages["_default_"] ?? emptyComponent;
	}

	public static getPermitMenu(): LazyExoticComponent<(props: PermitMenuProps) => JSX.Element> {
		return permitMenus[this.slug] ?? permitMenus["_default_"] ?? emptyComponent;
	}

	public static getPermitsHome(): LazyExoticComponent<() => JSX.Element> {
		return permitHomes[this.slug] ?? permitHomes["_default_"] ?? emptyComponent;
	}

	public static getPermitsTopMenu(): (LazyExoticComponent<(props: PermitsTopMenuProps) => JSX.Element>) {
		return permitTopMenus[this.slug] ?? permitTopMenus["_default_"] ?? emptyComponent;
	}
}
